/**
 * Modality Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { modalityService } from './modality.service'
import { tokenStorage } from '@/lib/api'
import type {
  CreateModalityData,
  UpdateModalityData,
  ModalitySearchFilters,
} from '../../domain/modality/types'

const QUERY_KEYS = {
  modalities: 'modalities',
  modalitiesActive: 'modalities-active',
  modality: (id: string) => ['modality', id],
}

/**
 * Hook for listing modalities with filters
 */
export function useModalities(filters?: ModalitySearchFilters) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: [QUERY_KEYS.modalities, filters],
    queryFn: () => modalityService.list(filters),
    enabled: hasToken,
  })
}

/**
 * Hook for listing active modalities (for dropdowns)
 */
export function useActiveModalities() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: [QUERY_KEYS.modalitiesActive],
    queryFn: () => modalityService.listActive(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook for getting a single modality
 */
export function useModality(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.modality(id),
    queryFn: () => modalityService.getById(id),
    enabled: !!id,
  })
}

/**
 * Hook for creating a modality
 */
export function useCreateModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateModalityData) => modalityService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalitiesActive] })
    },
  })
}

/**
 * Hook for updating a modality
 */
export function useUpdateModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateModalityData }) =>
      modalityService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalitiesActive] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modality(variables.id) })
    },
  })
}

/**
 * Hook for deleting a modality
 */
export function useDeleteModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => modalityService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalitiesActive] })
    },
  })
}

/**
 * Hook for updating modalities display order
 */
export function useUpdateModalitiesOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (orders: Array<{ id: string; displayOrder: number }>) =>
      modalityService.updateOrder(orders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalitiesActive] })
    },
  })
}

/**
 * Hook for toggling modality active status
 */
export function useToggleModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => modalityService.toggle(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.modalitiesActive] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modality(id) })
    },
  })
}

export { modalityService }
